import * as api from '@apis/user-discount-coupons';
import { useAuthentication } from '@hooks/authentication';
import { UserDiscountCoupon } from '@models/user-discount-coupon';
import { useUserDiscountCouponsStore } from '@stores/userDiscountCouponsStore';
import { useQuery } from '@tanstack/react-query';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { Button, Input, Select, Table, Tag, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { clearEmptyKeysFromObject } from '@utils/common';
import { DISCOUNT_TYPE_TEXT } from './DiscountCouponsPage';

const STATUS_TEXT = {
  unused: '미사용',
  used: '사용완료',
  expired: '만료',
};

const STATUS_COLOR = {
  unused: 'blue',
  used: 'green',
  expired: 'red',
};

const UserDiscountCouponsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const searchInputRef = useRef<any>();

  const store = useUserDiscountCouponsStore();
  useAuthentication();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label}이(가) 클립보드에 복사되었습니다.`);
    } catch (err) {
      // fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${label}이(가) 클립보드에 복사되었습니다.`);
      } catch (fallbackErr) {
        message.error('클립보드 복사에 실패했습니다.');
      }
      document.body.removeChild(textArea);
    }
  };

  const userDiscountCouponsQuery = useQuery({
    queryKey: [`user-discount-coupons`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchUserDiscountCoupons(store.searchQuery),
  });

  useEffect(() => {
    const searchResult = userDiscountCouponsQuery.data?.data;
    if (searchResult) {
      store.setUserDiscountCouponsContainer(searchResult);
    }
  }, [userDiscountCouponsQuery.data]);

  // 초기 로드 시 검색 실행
  useEffect(() => {
    const query = {
      page: 0,
      size: store.searchQuery.size,
      status: statusFilter,
    };
    store.updateSearchQuery(query);
  }, []); // 빈 의존성 배열로 컴포넌트 마운트 시 한 번만 실행

  const handleSearch = () => {
    const query = {
      page: 0,
      size: store.searchQuery.size,
      keyword: searchKeyword,
      status: statusFilter,
    };
    store.updateSearchQuery(clearEmptyKeysFromObject(query));
  };

  const handleReset = () => {
    setSearchKeyword('');
    setStatusFilter('all');
    store.resetSearchQuery();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    const query = {
      page: 0,
      size: store.searchQuery.size,
      keyword: searchKeyword,
      status: value,
    };
    store.updateSearchQuery(clearEmptyKeysFromObject(query));
  };

  const columns: ColumnsType<UserDiscountCoupon> = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: '_id',
      width: 120,
      render: (value: string) => <Button onClick={() => copyToClipboard(value, 'ID')}>ID복사</Button>,
    },
    {
      title: '사용자 정보',
      key: 'userInfo',
      width: 200,
      render: (record: UserDiscountCoupon) => (
        <div style={{ fontSize: '12px' }}>
          {record.userInfo && (
            <>
              <div>
                <strong>이름:</strong> {record.userInfo.profile?.name || '-'}
              </div>
              <div>
                <strong>연락처:</strong> {record.userInfo.profile?.phone || '-'}
              </div>
              <div>
                <Button onClick={() => copyToClipboard(record.userId, '유저 ID')}>ID복사</Button>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      title: '쿠폰명',
      dataIndex: ['couponInfo', 'name'],
      key: 'couponName',
      width: 200,
      render: (value: string) => <span style={{ fontSize: '12px' }}>{value}</span>,
    },
    {
      title: '할인 정보',
      width: '150px',
      render: (record: UserDiscountCoupon) => {
        const couponInfo = record.couponInfo;
        const discountType = DISCOUNT_TYPE_TEXT[couponInfo.discountType as keyof typeof DISCOUNT_TYPE_TEXT];
        let discountInfo = '';
        if (couponInfo.discountType === 'percentage') {
          discountInfo = `•${couponInfo?.discountPercent || '-'}% (최대 ${(
            couponInfo.maxDiscountAmount || 0
          ).toLocaleString()}원)`;
        } else {
          discountInfo = `${(couponInfo.discountAmount || 0).toLocaleString()}원`;
        }
        if (couponInfo.minPurchaseAmount) {
          discountInfo += `\n•최소주문금액: ${couponInfo.minPurchaseAmount.toLocaleString()}원`;
        }
        return (
          <div>
            <div>[{discountType}]</div>
            <div style={{ fontWeight: 'bold' }}>{discountInfo}</div>
          </div>
        );
      },
    },
    {
      title: '주문 ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 120,
      render: (value: string) =>
        value ? (
          <Button onClick={() => copyToClipboard(value, '주문 ID')}>ID복사</Button>
        ) : (
          <span style={{ fontSize: '12px', color: '#999' }}>-</span>
        ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string, record: UserDiscountCoupon) => {
        const now = new Date();
        const isUsed = record.usedAt;
        const isExpired = record.couponInfo.validUntil && new Date(record.couponInfo.validUntil) < now;

        let displayStatus: string;
        if (isUsed) {
          displayStatus = 'used';
        } else if (isExpired) {
          displayStatus = 'expired';
        } else {
          displayStatus = 'unused';
        }
        return (
          <Tag color={STATUS_COLOR[displayStatus as keyof typeof STATUS_COLOR]}>
            {STATUS_TEXT[displayStatus as keyof typeof STATUS_TEXT]}
          </Tag>
        );
      },
    },
    {
      title: '발급일',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      width: 120,
      render: (value: Date) => <span style={{ fontSize: '12px' }}>{dayjs(value).format('YYYY-MM-DD HH:mm')}</span>,
    },
    {
      title: '사용일',
      dataIndex: 'usedAt',
      key: 'usedAt',
      width: 120,
      render: (value: Date) => (
        <span style={{ fontSize: '12px' }}>{value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'}</span>
      ),
    },
    {
      title: '유효기간',
      key: 'validPeriod',
      width: 200,
      render: (record: UserDiscountCoupon) => {
        const validFrom = record.couponInfo.validFrom;
        const validUntil = record.couponInfo.validUntil;

        if (!validFrom && !validUntil) {
          return <span style={{ fontSize: '12px', color: '#999' }}>-</span>;
        }

        const fromText = validFrom ? dayjs(validFrom).format('YYYY-MM-DD HH:mm') : '-';
        const untilText = validUntil ? dayjs(validUntil).format('YYYY-MM-DD HH:mm') : '-';

        return (
          <span style={{ fontSize: '12px' }}>
            {fromText} ~ <br /> {untilText}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>할인 쿠폰 발행내역</h2>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          className={filterActiveClassName(!!store.searchQuery.keyword)}
          placeholder="사용자 ID, 이름, 연락처, 주문 ID"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 350 }}
        />
        <FilterSelect
          inactiveValue="all"
          placeholder="상태 필터"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          style={{ width: 120 }}>
          <Select.Option value="all">전체</Select.Option>
          <Select.Option value="unused">미사용</Select.Option>
          <Select.Option value="used">사용완료</Select.Option>
          <Select.Option value="expired">만료</Select.Option>
        </FilterSelect>
        <Button onClick={handleReset}>검색 초기화</Button>
        <div style={{ fontWeight: 'bold' }}>{`검색결과: ${store.searchResultContainer.total}개`}</div>
      </div>

      <Table
        columns={columns}
        dataSource={store.searchResultContainer.contents}
        loading={userDiscountCouponsQuery.isLoading}
        rowKey="_id"
        style={{ whiteSpace: 'pre-wrap' }}
        pagination={{
          current: (store.searchQuery.page || 0) + 1, // 0-based to 1-based for display
          pageSize: store.searchQuery.size,
          total: store.searchResultContainer.total,
          onChange: (page, pageSize) => {
            store.updateSearchQuery({
              page: page - 1, // 1-based to 0-based for API
              size: pageSize || 20,
            });
          },
        }}
        scroll={{ x: 1300 }}
      />
    </div>
  );
};

export default UserDiscountCouponsPage;
